import React, { Component } from "react";
import { FormGroup, Button, Label, Input } from "reactstrap";
import Select from "react-select";
import BiomarkerGeneSelect, { toBiomarkerGeneOptions } from '../ReusableComponent/BiomarkerGeneSelect';
import { connect } from 'react-redux';
import axiosInstance from "../configuration/Request";
import Loader from "react-loaders";
import TooltipComponent from "../ReusableComponent/TooltipComponent";
import { setCancerType } from "../../reducers/counterSlice";

const SURG_RAD_OPTIONS = [
  { value: 'AllSamples', label: 'All Patients' },
  { value: 'Surg', label: 'Surgery' },
  { value: 'Rad', label: 'Radiation' },
  { value: 'Both', label: 'Surgery and Radiation' },
];

const GENE_OPTIONS = [{ value: 'ACVR1B', label: 'ACVR1B' }];

const mapStateToProps = (state) => {
  return {
    value: state.counterSlice.value
  };
};

const mapDispatchToProps = { setCancerType };

class BioMarkerSelectionPage extends Component {
  constructor(props) {
    super(props);
    this.requestFallbackTimer = null;
    this.state = {
      selectedOption: null,
      selectedGene: null,
      geneOptions: [],
      geneOptionsLoading: false,
      bioMarkerOptions: [],
      loader: false,
      dropdownLoader: false,

      primarySelection: '',
      prognosisSecondarySelection: '',

      // Dynamic prognosis filter options from API
      prognosisMolecularTypeOptions: [],
      selectedPrognosisMolecularType: [],
      geneMolecularTypeOptions: [],
      selectedGeneMolecularType: [],
      prognosisCohortOptions: [],
      selectedPrognosisCohort: null,
      selectedPrognosisCohortEnabled: false,
      selectedPrognosisPopulationEnabled: true,
      selectedPrognosisConcordantEnabled: false,

      // Static treatment filters
      selectedSurgRad: SURG_RAD_OPTIONS[0],

      // Dynamic treatment filter options from API
      treatmentDrugOptions: [],
      selectedTreatmentDrugs: [],
      treatmentCohortOptions: [],
      selectedTreatmentCohort: null,

    };
  }

  componentDidMount() {
    this.setState({ loader: true, dropdownLoader: true });

    this.requestFallbackTimer = setTimeout(() => {
      this.setState({ loader: false, dropdownLoader: false });
    }, 8000);

    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/drug-informations?fields=main_repositioning`)
      .then(res => {
        let option = res.data.data.reduce((list, item) => {
          list.push({ value: item.id, label: item?.attributes?.main_repositioning });
          return list;
        }, []);
        option = option.filter((value, index, self) =>
          index === self.findIndex((t) => (
            t.label === value.label && value?.label !== 'NA'
          ))
        );
        this.setState({ bioMarkerOptions: option });
      })
      .catch(err => console.log(err))
      .finally(() => {
        clearTimeout(this.requestFallbackTimer);
        this.setState({ loader: false });
      });

    this.setState({ geneOptionsLoading: true });

    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/biomarker-unique-gene`)
      .then(res => {
        const geneOptions = toBiomarkerGeneOptions(res?.data?.data?.attributes?.unique_genes);

        this.setState(prevState => {
          const sameGenes = prevState.geneOptions.length === geneOptions.length
            && prevState.geneOptions.every((option, index) => option.value === geneOptions[index]?.value);

          return sameGenes ? null : { geneOptions };
        });
      })
      .catch(err => console.log(err))
      .finally(() => {
        this.setState(prevState => (prevState.geneOptionsLoading ? { geneOptionsLoading: false } : null));
      });

    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/gene-dropdown/projection-dropdowns`)
      .then(res => {
        const prognosis = res.data?.dropdowns?.prognosis || {};
        const treatment = res.data?.dropdowns?.treatment || {};

        const prognosisMolecularTypeOptions = prognosis.data_level || [];
        const prognosisCohortOptions = prognosis.cohort || [];
        const treatmentCohortOptions = treatment.cohort || [];
        const treatmentDrugOptions = (treatment.treatment || []).map(t => ({ value: t, label: t.replace(/\+/g, ' + ') }));

        const toOption = v => ({ value: v, label: v });
        const defaultPrognosisCohortVal = prognosisCohortOptions.includes('TCGA')
          ? 'TCGA' : (prognosisCohortOptions[0] || null);
        const defaultTreatmentCohortVal = treatmentCohortOptions.includes('TCGA')
          ? 'TCGA' : (treatmentCohortOptions[0] || null);
        const defaultPrognosisCohort = defaultPrognosisCohortVal ? toOption(defaultPrognosisCohortVal) : null;
        const defaultTreatmentCohort = defaultTreatmentCohortVal ? toOption(defaultTreatmentCohortVal) : null;

        this.setState({
          prognosisMolecularTypeOptions,
          geneMolecularTypeOptions: prognosisMolecularTypeOptions,
          selectedGeneMolecularType: [],
          prognosisCohortOptions,
          treatmentDrugOptions,
          treatmentCohortOptions,
          selectedTreatmentCohort: defaultTreatmentCohort,
        });
      })
      .catch(err => console.log(err))
      .finally(() => {
        clearTimeout(this.requestFallbackTimer);
        this.setState({ dropdownLoader: false });
      });
  }

  componentWillUnmount() {
    if (this.requestFallbackTimer) {
      clearTimeout(this.requestFallbackTimer);
    }
  }

  handleChange = (event) => {
    this.setState({ selectedOption: event });
  };

  handleGeneChange = (selected) => {
    this.setState({ selectedGene: selected || null });
  };

  handlePrimaryChange = (e) => {
    this.setState({
      primarySelection: e.target.value,
      prognosisSecondarySelection: '',
      selectedPrognosisMolecularType: [],
    });
  };

  handlePrognosisSecondaryChange = (e) => {
    this.setState({ prognosisSecondarySelection: e.target.value });
  };

  handlePrognosisMolecularTypeChange = (e) => {
    const { value, checked } = e.target;
    this.setState(prev => ({
      selectedPrognosisMolecularType: checked
        ? [...prev.selectedPrognosisMolecularType, value]
        : prev.selectedPrognosisMolecularType.filter(t => t !== value),
    }));
  };

  handleGeneMolecularTypeChange = (e) => {
    const { value, checked } = e.target;
    this.setState(prev => ({
      selectedGeneMolecularType: checked
        ? [...prev.selectedGeneMolecularType, value]
        : prev.selectedGeneMolecularType.filter(t => t !== value),
    }));
  };

  handlePrognosisCohortChange = (selected) => {
    this.setState({
      selectedPrognosisCohort: selected,
    });
  };

  handlePrognosisCohortToggle = () => {
    this.setState({
      selectedPrognosisCohortEnabled: true,
      selectedPrognosisPopulationEnabled: false,
      selectedPrognosisConcordantEnabled: false,
    });
  };

  handlePrognosisPopulationToggle = () => {
    this.setState({
      selectedPrognosisCohortEnabled: false,
      selectedPrognosisCohort: null,
      selectedPrognosisPopulationEnabled: true,
      selectedPrognosisConcordantEnabled: false,
    });
  };

  handlePrognosisConcordantToggle = () => {
    if (this.state.selectedPrognosisConcordantEnabled) {
      this.handlePrognosisPopulationToggle();
      return;
    }
    if (!this.state.selectedPrognosisCohort) return;
    this.setState({
      selectedPrognosisCohortEnabled: true,
      selectedPrognosisPopulationEnabled: true,
      selectedPrognosisConcordantEnabled: true,
    });
  };

  handleTreatmentDrugChange = (selected) => {
    this.setState({ selectedTreatmentDrugs: selected || [] });
  };

  handleSurgRadChange = (selected) => {
    this.setState({ selectedSurgRad: selected || SURG_RAD_OPTIONS[0] });
  };

  handleTreatmentCohortChange = (selected) => {
    this.setState({ selectedTreatmentCohort: selected });
  };

  checkSubmitDisabled = () => {
    const {
      selectedOption,
      selectedGene,
      primarySelection,
      prognosisSecondarySelection,
      selectedPrognosisMolecularType,
      selectedGeneMolecularType,
      geneMolecularTypeOptions,
      selectedPrognosisCohort,
      selectedTreatmentCohort,
    } = this.state;
    const { selectedPrognosisCohortEnabled } = this.state;

    if (!primarySelection) return true;
    if (!selectedOption) return true;
    if (primarySelection === 'gene') {
      if (!selectedGene) return true;
      if (geneMolecularTypeOptions.length > 0 && selectedGeneMolecularType.length === 0) return true;
      return false;
    }

    if (primarySelection === 'prognosis') {
      const needsCohort = !!this.state.selectedPrognosisCohortEnabled;
      const missingCohort = needsCohort && !selectedPrognosisCohort;
      return !prognosisSecondarySelection || selectedPrognosisMolecularType.length === 0 || missingCohort;
    }

    if (primarySelection === 'treatment') {
      return !selectedTreatmentCohort;
    }

    if (primarySelection === 'gene') return false;

    return false;
  };

  onProceedClick = () => {
    const {
      selectedOption,
      selectedGene,
      primarySelection,
      prognosisSecondarySelection,
      selectedPrognosisMolecularType,
      selectedGeneMolecularType,
      geneMolecularTypeOptions,
      selectedPrognosisCohort,
      selectedSurgRad,
      selectedTreatmentDrugs,
      selectedTreatmentCohort,
    } = this.state;

    if (primarySelection === 'gene') {
      const geneName = selectedGene?.value || 'ACVR1B';
      const expressionType = selectedGeneMolecularType.length > 0 ? selectedGeneMolecularType : geneMolecularTypeOptions;
      const cancerTypeLabel = selectedOption?.label || 'Non-Small Cell Lung Cancer';
      const searchParams = new URLSearchParams({
        geneName,
        expressionType: expressionType.join(','),
        cancerTypeLabel,
      });
      this.props.history.push({
        pathname: '/app/bioMarkerGeneResult',
        search: `?${searchParams.toString()}`,
        state: { geneName, expressionType, cancerTypeLabel },
      });
      return;
    }

    const route = primarySelection === 'treatment' ? '/app/bioMarkerTreatmentResult' : '/app/bioMarkerResult';

    const payload = {
      bioMarker: selectedOption,
      flowType: primarySelection,
      prognosisDetails: primarySelection === 'prognosis' ? (() => {
        const pd = {
          prognosisType: prognosisSecondarySelection,
          expressionType: selectedPrognosisMolecularType,
        };
        // include cohortEnabled flag so result page mirrors the UI state
        pd.cohortEnabled = !!this.state.selectedPrognosisCohortEnabled;
        if (this.state.selectedPrognosisCohortEnabled && selectedPrognosisCohort) {
          pd.cohort = selectedPrognosisCohort?.value || selectedPrognosisCohort;
        }
        if (this.state.selectedPrognosisPopulationEnabled) pd.population = true;
        if (this.state.selectedPrognosisConcordantEnabled) pd.concordant = true;
        return pd;
      })() : null,
      treatments: primarySelection === 'treatment' ? {
        drugs: selectedTreatmentDrugs.map(t => t.value),
        surgRad: selectedSurgRad?.value || 'AllSamples',
        cohort: selectedTreatmentCohort?.value || selectedTreatmentCohort,
      } : null,
    };

    const searchParams = new URLSearchParams({
      cancerType: selectedOption?.label || 'NSCLC',
    });

    if (primarySelection === 'prognosis') {
      searchParams.set('prognosisType', prognosisSecondarySelection);
      searchParams.set('molecularType', selectedPrognosisMolecularType.join(','));
      searchParams.set('cohortEnabled', String(!!this.state.selectedPrognosisCohortEnabled));
      searchParams.set('population', String(!!this.state.selectedPrognosisPopulationEnabled));
      searchParams.set('concordant', String(!!this.state.selectedPrognosisConcordantEnabled));
      if (selectedPrognosisCohort) searchParams.set('cohort', selectedPrognosisCohort?.value || selectedPrognosisCohort);
    } else {
      searchParams.set('drugs', selectedTreatmentDrugs.map(t => t.value).join(','));
      searchParams.set('surgRad', selectedSurgRad?.value || 'AllSamples');
      if (selectedTreatmentCohort) searchParams.set('cohort', selectedTreatmentCohort?.value || selectedTreatmentCohort);
    }

    this.props.history.push({ pathname: route, search: `?${searchParams.toString()}`, state: payload });
  };

  render() {
    const {
      selectedOption,
      selectedGene,
      geneOptions,
      geneOptionsLoading,
      primarySelection,
      prognosisSecondarySelection,
      selectedPrognosisMolecularType,
      prognosisMolecularTypeOptions,
      geneMolecularTypeOptions,
      selectedGeneMolecularType,
      prognosisCohortOptions,
      selectedPrognosisCohort,
      selectedPrognosisCohortEnabled,
      selectedPrognosisPopulationEnabled,
      selectedPrognosisConcordantEnabled,
      selectedSurgRad,
      treatmentDrugOptions,
      selectedTreatmentDrugs,
      treatmentCohortOptions,
      selectedTreatmentCohort,
      dropdownLoader,
    } = this.state;

    return (
      <>
        <div className="CancerTypeContainer" style={{ alignItems: 'flex-start', height: 'auto', minHeight: '70vh', paddingTop: '32px', paddingBottom: '32px' }}>
          <div style={{ width: '100%', maxWidth: '560px' }}>
            <div className="cancerType-header">Select Biomarker</div>
            <div className="cancerType-subheader">Select the biomarker you would like to explore</div>
            <div style={{ marginTop: '24px' }}>
              <FormGroup>
                <Select
                  styles={{
                    control: (baseStyles) => ({ ...baseStyles, height: '48px', cursor: 'pointer' }),
                    option: (base) => ({ ...base, cursor: 'pointer' }),
                  }}
                  value={selectedOption}
                  onChange={this.handleChange}
                  options={this.state.bioMarkerOptions}
                />
              </FormGroup>

              {selectedOption && (
                <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #eaeaea', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                  {/* View By - static */}
                  <FormGroup tag="fieldset">
                    <legend style={{ fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid #ddd', paddingBottom: '8px', marginBottom: '16px' }}>View By</legend>
                    <FormGroup check>
                      <Label check>
                        <Input type="radio" name="primaryFlow" value="prognosis" checked={primarySelection === 'prognosis'} onChange={this.handlePrimaryChange} />{' '}
                        By Prognosis
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input type="radio" name="primaryFlow" value="treatment" checked={primarySelection === 'treatment'} onChange={this.handlePrimaryChange} />{' '}
                        By Treatment
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input type="radio" name="primaryFlow" value="gene" checked={primarySelection === 'gene'} onChange={this.handlePrimaryChange} />{' '}
                        By Gene
                      </Label>
                    </FormGroup>
                  </FormGroup>

                  {/* Gene sub-filters */}
                  {primarySelection === 'gene' && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '8px' }}>Select Gene</div>
                      <BiomarkerGeneSelect
                        options={geneOptions.length > 0 ? geneOptions : GENE_OPTIONS}
                        value={selectedGene}
                        onChange={this.handleGeneChange}
                        placeholder={geneOptionsLoading ? 'Loading genes...' : 'Select gene...'}
                        isLoading={geneOptionsLoading}
                        isClearable
                        styles={{
                          control: (base) => ({ ...base, minHeight: '40px', cursor: 'pointer' }),
                          option: (base) => ({ ...base, cursor: 'pointer' }),
                          placeholder: (base) => ({ ...base, fontSize: '0.85rem' }),
                        }}
                      />

                      {geneMolecularTypeOptions.length > 0 && (
                        <FormGroup tag="fieldset" style={{ marginTop: '16px' }}>
                          <legend style={{ fontSize: '0.9rem', fontWeight: 600 }}>Molecular Type</legend>
                          <div className="d-flex flex-wrap gap-3 mt-2">
                            {geneMolecularTypeOptions.map(type => (
                              <FormGroup check key={type}>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    value={type}
                                    checked={selectedGeneMolecularType.includes(type)}
                                    onChange={this.handleGeneMolecularTypeChange}
                                  />{' '}
                                  {type}
                                </Label>
                              </FormGroup>
                            ))}
                          </div>
                        </FormGroup>
                      )}
                    </div>
                  )}

                  {/* Prognosis sub-filters */}
                  {primarySelection === 'prognosis' && (
                    <div style={{ marginLeft: '24px', paddingLeft: '16px' }}>
                      {/* Prognosis Type - static */}
                      <FormGroup tag="fieldset">
                        <legend style={{ fontSize: '0.9rem', fontWeight: 600 }}>Prognosis Type</legend>
                        <FormGroup check inline>
                          <Label check>
                            <Input type="radio" name="prognosisSecondary" value="good" checked={prognosisSecondarySelection === 'good'} onChange={this.handlePrognosisSecondaryChange} />{' '}
                            Good prognosis
                          </Label>
                        </FormGroup>
                        <FormGroup check inline>
                          <Label check>
                            <Input type="radio" name="prognosisSecondary" value="poor" checked={prognosisSecondarySelection === 'poor'} onChange={this.handlePrognosisSecondaryChange} />{' '}
                            Poor prognosis
                          </Label>
                        </FormGroup>
                      </FormGroup>

                      {/* Molecular Type - dynamic from API */}
                      {prognosisMolecularTypeOptions.length > 0 && (
                        <FormGroup tag="fieldset" style={{ marginTop: '12px' }}>
                          <legend style={{ fontSize: '0.9rem', fontWeight: 600 }}>Molecular Type</legend>
                          <div className="d-flex flex-wrap gap-3 mt-2">
                            {prognosisMolecularTypeOptions.map(type => (
                              <FormGroup check key={type}>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    value={type}
                                    checked={selectedPrognosisMolecularType.includes(type)}
                                    onChange={this.handlePrognosisMolecularTypeChange}
                                  />{' '}
                                  {type}
                                </Label>
                              </FormGroup>
                            ))}
                          </div>
                        </FormGroup>
                      )}

                      {/* Cohort - dynamic from API (checkbox to enable) */}
                      {prognosisCohortOptions.length > 0 && (
                        <FormGroup style={{ marginTop: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                            <label className="bmt-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="radio"
                                name="prognosisCohort"
                                checked={!!selectedPrognosisCohortEnabled}
                                onClick={selectedPrognosisConcordantEnabled ? this.handlePrognosisCohortToggle : undefined}
                                onChange={this.handlePrognosisCohortToggle}
                              />
                              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Cohort</span>
                            </label>
                          </div>
                          <Select
                            isDisabled={!selectedPrognosisCohortEnabled}
                            options={prognosisCohortOptions.map(c => ({ value: c, label: c }))}
                            value={selectedPrognosisCohort}
                            onChange={this.handlePrognosisCohortChange}
                            placeholder="Select cohort..."
                            styles={{
                              control: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                              option: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                            }}
                          />
                          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label className="bmt-checkbox-label">
                              <input type="radio" name="prognosisPopulation" checked={!!selectedPrognosisPopulationEnabled} onClick={selectedPrognosisConcordantEnabled ? this.handlePrognosisPopulationToggle : undefined} onChange={this.handlePrognosisPopulationToggle} />{' '}
                              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Population Projection</span>
                            </label>
                            <label className="bmt-checkbox-label">
                              <input type="radio" name="prognosisConcordant" disabled={!selectedPrognosisCohort} checked={!!selectedPrognosisConcordantEnabled} readOnly onClick={this.handlePrognosisConcordantToggle} />{' '}
                              <span >Concordant Results of Selected Cohort and Population Projection</span>
                            </label>
                          </div>
                        </FormGroup>
                      )}
                    </div>
                  )}

                  {/* Treatment sub-filters */}
                  {primarySelection === 'treatment' && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '8px' }}>Treatment</div>
                      {/* Drug treatments - dynamic from API */}
                      <legend style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', marginTop: '12px' }}>Select Drug Treatments</legend>
                      <Select
                        isMulti
                        options={treatmentDrugOptions}
                        value={selectedTreatmentDrugs}
                        onChange={this.handleTreatmentDrugChange}
                        placeholder="Select drug treatments..."
                        isLoading={dropdownLoader}
                        styles={{
                          control: (base) => ({ ...base, fontSize: '0.85rem', minHeight: '40px', cursor: 'pointer' }),
                          option: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                          multiValue: (base) => ({ ...base, fontSize: '0.8rem' }),
                          placeholder: (base) => ({ ...base, fontSize: '0.85rem' }),
                        }}
                      />
                      <FormGroup style={{ marginTop: '12px' }}>
                        <legend style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Surgery / Radiation</legend>
                        <Select
                          options={SURG_RAD_OPTIONS}
                          value={selectedSurgRad}
                          onChange={this.handleSurgRadChange}
                          styles={{
                            control: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                            option: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                          }}
                        />
                      </FormGroup>
                      {/* Cohort - dynamic from API */}
                      {treatmentCohortOptions.length > 0 && (
                        <FormGroup style={{ marginTop: '12px' }}>
                          <legend style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '6px' }}>Cohort</legend>
                          <Select
                            options={treatmentCohortOptions.map(c => ({ value: c, label: c }))}
                            value={selectedTreatmentCohort}
                            onChange={this.handleTreatmentCohortChange}
                            placeholder="Select cohort..."
                            styles={{
                              control: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                              option: (base) => ({ ...base, fontSize: '0.85rem', cursor: 'pointer' }),
                            }}
                          />
                        </FormGroup>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-center mt-4">
              <Button
                className="mb-2 me-2 w-100"
                color="primary"
                disabled={this.checkSubmitDisabled()}
                style={{ borderRadius: '4px', height: '48px', fontSize: '1rem' }}
                onClick={this.onProceedClick}
              >
                Proceed
              </Button>
            </div>
          </div>
        </div>

        {(this.state.loader || this.state.dropdownLoader) && (
          <div className="backdrop">
            <div className="loader">
              <Loader type="ball-pulse-rise" />
            </div>
          </div>
        )}
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(BioMarkerSelectionPage);
